<template>
  <div>
    <!-- Jobboards -->
    <div class="card mb-3">
      <div class="card-header">
        <h5 class="card-title mb-0"><i class="fas fa-globe mr-2"></i>Jobboards</h5>
      </div>
      <div class="card-body">
        <div v-if="offer.jobboards && offer.jobboards.length">
          <div v-for="jb in offer.jobboards" :key="jb.id" class="d-flex justify-content-between align-items-center mb-2">
            <span>{{ jb.label }}</span>
            <b-badge :variant="getJobboardStatusVariant(jb.pivot.status)">
              {{ jb.pivot.status }}
            </b-badge>
          </div>
        </div>
        <span v-else class="text-muted">No jobboards</span>
      </div>
    </div>

    <!-- Diffusions -->
    <div class="card mb-3">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="card-title mb-0"><i class="fas fa-share-alt mr-2"></i>Diffusions</h5>
        <b-badge variant="secondary">{{ diffusions.length }}</b-badge>
      </div>
      <div class="card-body">
        <div v-if="diffusions.length">
          <b-table
            :items="diffusions"
            :fields="diffusionFields"
            small
            hover
            responsive
          >
            <template #cell(platform_name)="{ item }">
              <b-badge variant="primary">{{ item.platform_name }}</b-badge>
            </template>
            <template #cell(status)="{ item }">
              <b-badge :variant="getDiffusionStatusVariant(item.status)">{{ item.status }}</b-badge>
            </template>
            <template #cell(offer_url)="{ item }">
              <div v-if="item.offer_url" class="d-flex align-items-center">
                <a :href="item.offer_url" target="_blank" class="small text-truncate" style="max-width: 250px;" :title="item.offer_url">
                  {{ item.offer_url }}
                </a>
                <CopyButton :text="item.offer_url" />
              </div>
              <span v-else class="text-muted small">-</span>
            </template>
            <template #cell(billed)="{ item }">
              <b-form-checkbox
                :checked="item.billed"
                switch
                size="sm"
                @change="toggleBilled(item)"
                :disabled="isUpdating === item.id"
              />
            </template>
            <template #cell(published_at)="{ item }">
              <small class="text-muted">{{ formatDate(item.published_at) }}</small>
            </template>
            <template #cell(xml)="{ item }">
              <b-button
                v-if="getFeedUrl(item.platform_name)"
                variant="outline-info"
                size="sm"
                class="mr-1"
                :href="getFeedUrl(item.platform_name)"
                target="_blank"
                title="View XML feed"
              >
                <i class="fas fa-eye"></i>
              </b-button>
              <b-button
                variant="outline-secondary"
                size="sm"
                @click="generateFeed(item.platform_name)"
                :disabled="generatingFeeds[item.platform_name]"
                title="Generate XML feed"
              >
                <b-spinner v-if="generatingFeeds[item.platform_name]" small></b-spinner>
                <i v-else class="fas fa-sync-alt"></i>
              </b-button>
            </template>
            <template #cell(actions)="{ item }">
              <b-button
                v-if="item.status !== 'published'"
                variant="outline-success"
                size="sm"
                class="mr-1"
                @click="updateStatus(item, 'published')"
                :disabled="isUpdating === item.id"
                title="Publish"
              >
                <b-spinner v-if="isUpdating === item.id" small></b-spinner>
                <i v-else class="fas fa-check"></i>
              </b-button>
              <b-button
                v-if="item.status !== 'unpublished'"
                variant="outline-danger"
                size="sm"
                @click="updateStatus(item, 'unpublished')"
                :disabled="isUpdating === item.id"
                title="Unpublish"
              >
                <b-spinner v-if="isUpdating === item.id" small></b-spinner>
                <i v-else class="fas fa-times"></i>
              </b-button>
            </template>
          </b-table>
        </div>
        <span v-else class="text-muted">No diffusions</span>
      </div>
    </div>

    <!-- Indeed -->
    <div class="card mb-3">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="card-title mb-0"><i class="fab fa-searchengin mr-2"></i>Indeed</h5>
      </div>
      <div class="card-body">
        <b-button
          variant="outline-primary"
          size="sm"
          @click="pushOfferToIndeed"
          :disabled="pushingIndeed"
        >
          <b-spinner v-if="pushingIndeed" small></b-spinner>
          <i v-else class="fas fa-upload mr-1"></i>Push Indeed
        </b-button>
        <b-button
          variant="outline-danger"
          size="sm"
          class="ml-2"
          @click="expireOfferOnIndeed"
          :disabled="expiringIndeed"
        >
          <b-spinner v-if="expiringIndeed" small></b-spinner>
          <i v-else class="fas fa-ban mr-1"></i>Expire Indeed
        </b-button>
      </div>
    </div>

    <!-- Feed Out Eligibility -->
    <div class="card mb-3">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="card-title mb-0"><i class="fas fa-rss mr-2"></i>Feed Out Status</h5>
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="fetchFeedOutEligibility"
          :disabled="isLoadingFeedOut"
          title="Refresh"
        >
          <b-spinner v-if="isLoadingFeedOut" small></b-spinner>
          <i v-else class="fas fa-sync-alt"></i>
        </b-button>
      </div>
      <div class="card-body">
        <div v-if="isLoadingFeedOut" class="text-center py-3">
          <b-spinner small></b-spinner>
          <span class="ml-2">Checking eligibility...</span>
        </div>
        <div v-else-if="feedOutEligibility">
          <div
            v-for="(feed, key) in feedOutEligibility"
            :key="key"
            class="d-flex justify-content-between align-items-center mb-2 feed-out-item"
            :class="{ 'feed-out-eligible': feed.eligible, 'feed-out-ineligible': !feed.eligible }"
          >
            <span class="feed-out-label">{{ feed.label }}</span>
            <div class="d-flex align-items-center">
              <b-badge v-if="feed.eligible" variant="success" class="mr-1">
                <i class="fas fa-check"></i>
              </b-badge>
              <b-badge
                v-else
                variant="danger"
                class="mr-1 cursor-pointer"
                @click="showFeedOutMissing(feed)"
                v-b-tooltip.hover
                :title="getMissingTooltip(feed)"
              >
                <i class="fas fa-times"></i> {{ feed.missing.length }}
              </b-badge>
            </div>
          </div>
        </div>
        <div v-else class="text-muted small">
          Click refresh to check feed out eligibility
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BBadge, BTable, BButton, BSpinner, BFormCheckbox, VBTooltip } from "bootstrap-vue";
import CopyButton from "@/components/CopyButton.vue";

export default {
  components: { BBadge, BTable, BButton, BSpinner, BFormCheckbox, CopyButton },
  directives: { "b-tooltip": VBTooltip },
  props: {
    offer: { type: Object, required: true },
    offerId: { type: [String, Number], required: true }
  },
  data() {
    return {
      isUpdating: null,
      feedOutEligibility: null,
      isLoadingFeedOut: false,
      pushingIndeed: false,
      expiringIndeed: false,
      feeds: [],
      generatingFeeds: {},
      diffusionFields: [
        { key: "platform_name", label: "Platform" },
        { key: "status", label: "Status" },
        { key: "offer_url", label: "Offer URL" },
        { key: "billed", label: "Billed" },
        { key: "published_at", label: "Published" },
        { key: "xml", label: "XML" },
        { key: "actions", label: "Actions" }
      ]
    };
  },
  computed: {
    diffusions() {
      return this.offer.diffusions || [];
    },
    feedUrlMap() {
      const map = {};
      this.feeds.forEach(f => { map[f.platform_name] = f.feed_url; });
      return map;
    }
  },
  created() {
    this.fetchFeedOutEligibility();
    this.fetchFeeds();
  },
  methods: {
    getJobboardStatusVariant(status) {
      const v = { published: "success", pending: "warning", unpublished: "secondary", rejected: "danger" };
      return v[status] || "secondary";
    },
    getDiffusionStatusVariant(status) {
      const v = { published: "success", unpublished: "secondary", pending: "warning" };
      return v[status] || "secondary";
    },
    formatDate(dateStr) {
      if (!dateStr) return "-";
      return new Date(dateStr).toLocaleDateString();
    },
    async updateStatus(item, status) {
      this.isUpdating = item.id;
      try {
        const { data } = await this.$http.put(`/enroll_admin/diffusions/${item.id}/status`, { status });
        item.status = data.status;
        item.published_at = data.published_at;
        item.unpublished_at = data.unpublished_at;
        this.$emit("diffusion-updated");
      } catch {
        this.$bvToast.toast("Failed to update diffusion status", {
          title: "Error", variant: "danger", solid: true
        });
      } finally {
        this.isUpdating = null;
      }
    },
    async toggleBilled(item) {
      const newBilled = !item.billed;
      this.isUpdating = item.id;
      try {
        const { data } = await this.$http.put(`/enroll_admin/diffusions/${item.id}/billing`, { billed: newBilled });
        item.billed = data.billed;
        item.billed_at = data.billed_at;
      } catch {
        this.$bvToast.toast("Failed to update billing", {
          title: "Error", variant: "danger", solid: true
        });
      } finally {
        this.isUpdating = null;
      }
    },
    getFeedUrl(platformName) {
      return this.feedUrlMap[platformName] || null;
    },
    async fetchFeeds() {
      try {
        const { data } = await this.$http.get("/enroll_admin/diffusion-feeds");
        this.feeds = data.feeds;
      } catch {
        // non-critical
      }
    },
    async generateFeed(platformName) {
      this.$set(this.generatingFeeds, platformName, true);
      try {
        await this.$http.post("/enroll_admin/diffusion-feeds/generate", { platform_name: platformName });
        this.$bvToast.toast(`Feed generation dispatched for ${platformName}`, {
          title: "Success", variant: "success", solid: true
        });
      } catch {
        this.$bvToast.toast(`Failed to generate feed for ${platformName}`, {
          title: "Error", variant: "danger", solid: true
        });
      } finally {
        this.$set(this.generatingFeeds, platformName, false);
      }
    },
    async fetchFeedOutEligibility() {
      this.isLoadingFeedOut = true;
      try {
        const { data } = await this.$http.get(`/enroll_admin/offer/${this.offerId}/feed-out-eligibility`);
        this.feedOutEligibility = data;
      } catch {
        // non-critical
      } finally {
        this.isLoadingFeedOut = false;
      }
    },
    getMissingTooltip(feed) {
      if (!feed.missing || !feed.missing.length) return "";
      return feed.missing.map(m => m.message).join("\n");
    },
    async pushOfferToIndeed() {
      this.pushingIndeed = true;
      try {
        await this.$http.post(`/enroll_admin/offer/${this.offerId}/push-indeed`);
        this.$bvToast.toast("Push Indeed lancé", {
          title: "Indeed",
          variant: "success",
          solid: true
        });
      } catch (e) {
        this.$bvToast.toast("Échec du push Indeed", {
          title: "Indeed",
          variant: "danger",
          solid: true
        });
      } finally {
        this.pushingIndeed = false;
      }
    },
    async expireOfferOnIndeed() {
      this.expiringIndeed = true;
      try {
        await this.$http.post(`/enroll_admin/offer/${this.offerId}/expire-indeed`);
        this.$bvToast.toast("Expire Indeed lancé", {
          title: "Indeed",
          variant: "success",
          solid: true
        });
      } catch (e) {
        this.$bvToast.toast("Échec de l'expiration Indeed", {
          title: "Indeed",
          variant: "danger",
          solid: true
        });
      } finally {
        this.expiringIndeed = false;
      }
    },
    showFeedOutMissing(feed) {
      const missingList = feed.missing.map(m => `\u2022 ${m.message}`).join("\n");
      this.$bvModal.msgBoxOk(missingList, {
        title: `${feed.label} - Missing Requirements`,
        size: "md",
        buttonSize: "sm",
        okVariant: "secondary",
        headerClass: "border-bottom-0",
        footerClass: "border-top-0",
        bodyClass: "feed-out-missing-body"
      });
    }
  }
};
</script>

<style scoped>
.card-title { font-size: 1rem; }
.feed-out-item { padding: 4px 8px; border-radius: 4px; font-size: 0.875rem; }
.feed-out-label { font-size: 0.8rem; }
.feed-out-eligible { background: rgba(40, 167, 69, 0.1); }
.feed-out-ineligible { background: rgba(220, 53, 69, 0.05); }
.cursor-pointer { cursor: pointer; }
</style>
